import { useRef } from 'react';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import useApiEndpoints from 'src/context/useApiEndpoints';
import useApi from '../useApi';
import { useEffect } from 'react';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { mapStowagePlans } from 'src/factory/Stowage/StowagePlanFactory';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import { getStowageDataItems } from 'src/utils/stowage/stowagePlanChangeUtils/stowageDataItemsBuilder';
import useGetStowageApi from './useGetStowageApi';
import usePreloadBaseStowageData from 'src/hooks/stowage/usePreloadBaseStowageData';
import { updateStowageDataSimple } from 'src/utils/stowage/updateStowageDataUtils';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { getMasterStowagePlan, fillMissingImosSyncDateFromParents } from 'src/utils/stowage/stowagePlanUtils';
import { useVesselStore } from 'src/context/VesselContext';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { state, getApiData } = useApi();
    const { setLoadedMasterStowagePlan, setMasterPlanLoadState, setMasterPlanLoadStateImo } = useStowagePlanStore();
    const { getStowagePlanChanges } = useGetStowageApi();
    const { processApiEndpointError } = apiEndpointUtils();
    const { getPreloadedData } = usePreloadBaseStowageData();

    // Track the currently selected vessel so stale async responses can be discarded
    const { selectedVessel } = useVesselStore();
    const currentImoRef = useRef<number | null>(null);
    currentImoRef.current = selectedVessel?.imoNumber ?? null;

    const getMasterStowagePlanFromApi = async (vessel: Vessel) => {
        const imoNumber = vessel.imoNumber;
        const baseDataResult = await getPreloadedData();
        if (!baseDataResult.success) {
            console.error('Failed to load base stowage data:', baseDataResult.error);
            if (currentImoRef.current === imoNumber) {
                setMasterPlanLoadState('error');
                setMasterPlanLoadStateImo(imoNumber);
            }
            return { success: false, error: baseDataResult.error };
        }

        try {
            const result = await getApiData({
                endpoint: apiEndpoints['getMasterStowagePlan'].url,
                queryParam: buildQueryParams({ imoNumber }),
                additionalInfo: { imoNumber },
            });

            // Discard response if the user has already switched to a different vessel
            if (currentImoRef.current !== imoNumber) {
                return { success: false, error: 'Vessel changed during load' };
            }

            if (!Array.isArray(result) || result.length === 0) {
                setLoadedMasterStowagePlan(null);
                setMasterPlanLoadState('not-found');
                setMasterPlanLoadStateImo(imoNumber);
                console.warn(`No master stowage plan data returned for vessel ${imoNumber}`);
                return { success: false, error: 'No master stowage plan data returned' };
            }

            const bms = result as StowagePlanBM[];
            const _stowagePlans = mapStowagePlans(bms);
            const _masterStowagePlan = getMasterStowagePlan(_stowagePlans);

            if (!_masterStowagePlan) {
                setLoadedMasterStowagePlan(null);
                setMasterPlanLoadState('not-found');
                setMasterPlanLoadStateImo(imoNumber);
                console.warn(`No master stowage plan found for vessel ${imoNumber}`);
                return { success: false, error: 'No master stowage plan found' };
            }

            fillMissingImosSyncDateFromParents(_masterStowagePlan, _stowagePlans);

            const stowagePlanChanges = await getStowagePlanChanges(_masterStowagePlan.stowagePlanGuid);

            // Guard again after the second await
            if (currentImoRef.current !== imoNumber) {
                return { success: false, error: 'Vessel changed during load' };
            }

            const { stowagePlanRotationPortCalls, updatedStowagePlan } = getStowageDataItems(stowagePlanChanges, _stowagePlans, _masterStowagePlan);
            await updateStowageDataSimple(
                updatedStowagePlan,
                stowagePlanRotationPortCalls,
                updatedStowagePlan.cargoes,
                baseDataResult.data.products,
                baseDataResult.data.productCategories,
                baseDataResult.data.stowageRuleMaps,
                baseDataResult.data.portNames,
                baseDataResult.data.berthNames,
                vessel
            );

            if (currentImoRef.current !== imoNumber) {
                return { success: false, error: 'Vessel changed during load' };
            }

            setLoadedMasterStowagePlan({ ...updatedStowagePlan });
            setMasterPlanLoadState('loaded');
            setMasterPlanLoadStateImo(imoNumber);

            return { success: true, data: updatedStowagePlan };
        } catch (error) {
            if (currentImoRef.current === imoNumber) {
                setLoadedMasterStowagePlan(null);
                setMasterPlanLoadState('error');
                setMasterPlanLoadStateImo(imoNumber);
            }
            console.error('Error loading master stowage plan:', error);
            return { success: false, error };
        }
    };

    useEffect(() => {
        (async () => {
            if (state.status !== 'success') {
                processApiEndpointError(state);
            }
        })();
    }, [state]);

    return { getMasterStowagePlanFromApi };
};
